import * as React from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import CssBaseline from "@mui/material/CssBaseline";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { getAccountLogo } from "../../shared/accountLogo";
import {
  Alert,
  Button,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import AccountCircle from "@mui/icons-material/AccountCircle";
import { useClient } from "../../contexts/ClientContext";
import useVersionCheck from "../../hooks/useVersionCheck";
import { PERMISSIONS } from "../constants";

const Main = styled("main")(({ theme }) => ({
  flexGrow: 1,
  width: "100%",
  minHeight: "100vh",
  backgroundColor: "#fafafa",
}));

const ToolbarOffset = styled("div")(({ theme }) => ({
  ...theme.mixins.toolbar,
}));

export default function Layout({ children }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    clientInfo,
    logout,
    hasPermission,
    refreshSession,
    sessionWarningKey,
  } = useClient();
  const { updateAvailable, reload } = useVersionCheck();
  const { logo, width: logoWidth } = getAccountLogo(clientInfo?.account);
  const [profileAnchorEl, setProfileAnchorEl] = React.useState(null);
  const [language, setLanguage] = React.useState(i18n.resolvedLanguage || "en");
  const [languageAnchorEl, setLanguageAnchorEl] = React.useState(null);

  const handleOpenProfileMenu = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleCloseProfileMenu = () => {
    setProfileAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleCloseProfileMenu();
  };

  const handleOpenProfile = () => {
    navigate("/profile");
    handleCloseProfileMenu();
  };

  const handleOpenLanguageMenu = (event) => {
    setLanguageAnchorEl(event.currentTarget);
  };

  const handleCloseLanguageMenu = () => {
    setLanguageAnchorEl(null);
  };

  const handleLanguageSelect = (nextLanguage) => {
    setLanguage(nextLanguage);
    i18n.changeLanguage(nextLanguage);
    handleCloseLanguageMenu();
  };

  const englishFlagSvg = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 32 32"
      preserveAspectRatio="xMidYMid meet"
    >
      <rect width="30" height="24" x="1" y="4" fill="#071b65" rx="4" ry="4" />
      <path
        fill="#fff"
        d="M5.101 4H5a3.99 3.99 0 0 0-3.933 3.334L26.899 28H27a3.99 3.99 0 0 0 3.933-3.334z"
      />
      <path
        fill="#b92932"
        d="M22.25 19h-2.5l9.934 7.947c.387-.353.704-.777.929-1.257l-8.363-6.691ZM1.387 6.309 9.75 13h2.5L2.316 5.053a4 4 0 0 0-.929 1.257Z"
      />
      <path
        fill="#fff"
        d="M5 28h.101L30.933 7.334A3.99 3.99 0 0 0 27 4h-.101L1.067 24.666A3.99 3.99 0 0 0 5 28"
      />
      <path fill="#fff" d="M13 4h6v24h-6z" />
      <path fill="#fff" d="M1 13h30v6H1z" />
      <path fill="#b92932" d="M14 4h4v24h-4z" />
      <path
        fill="#b92932"
        d="M31 14v4H1v-4zM28.222 4.21 19 11.586V13h.75l9.943-7.94a4 4 0 0 0-1.471-.85M2.328 26.957a4 4 0 0 0 1.447.832L13 20.409v-1.408h-.75z"
      />
      <path
        d="M27 4H5a4 4 0 0 0-4 4v16a4 4 0 0 0 4 4h22a4 4 0 0 0 4-4V8a4 4 0 0 0-4-4m3 20c0 1.654-1.346 3-3 3H5c-1.654 0-3-1.346-3-3V8c0-1.654 1.346-3 3-3h22c1.654 0 3 1.346 3 3z"
        opacity=".15"
      />
      <path
        fill="#fff"
        d="M27 5H5a3 3 0 0 0-3 3v1a3 3 0 0 1 3-3h22a3 3 0 0 1 3 3V8a3 3 0 0 0-3-3"
        opacity=".2"
      />
    </svg>
  );

  const greekFlagSvg = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 32 32"
      preserveAspectRatio="xMidYMid meet"
    >
      <rect width="30" height="24" x="1" y="4" fill="#fff" rx="4" ry="4" />
      <path
        fill="#295cab"
        d="M1.244 6.67h29.512A3.99 3.99 0 0 0 27 4H5a3.99 3.99 0 0 0-3.756 2.67M1 9.34h30v2.67H1zM1 14.68h30v2.67H1zM1 20.02h30v2.67H1zM1.253 25.36A3.99 3.99 0 0 0 5 28h22a3.99 3.99 0 0 0 3.747-2.64z"
      />
      <path fill="#295cab" d="M14.35 4H5a4 4 0 0 0-4 4v9.35h13.35z" />
      <path fill="#fff" d="M1 9.34h13.35v2.67H1z" />
      <path fill="#fff" d="M9.01 4v13.35H6.34V4z" />
      <path
        d="M27 4H5a4 4 0 0 0-4 4v16a4 4 0 0 0 4 4h22a4 4 0 0 0 4-4V8a4 4 0 0 0-4-4m3 20c0 1.654-1.346 3-3 3H5c-1.654 0-3-1.346-3-3V8c0-1.654 1.346-3 3-3h22c1.654 0 3 1.346 3 3z"
        opacity=".15"
      />
      <path
        fill="#fff"
        d="M27 5H5a3 3 0 0 0-3 3v1a3 3 0 0 1 3-3h22a3 3 0 0 1 3 3V8a3 3 0 0 0-3-3"
        opacity=".2"
      />
    </svg>
  );

  const languageFlag = language === "el" ? greekFlagSvg : englishFlagSvg;

  const navItems = [
    {
      label: t("nav.riskAssessment"),
      path: "/map",
      permission: PERMISSIONS.RISK_MAP,
    },
    {
      label: t("nav.riskAssessment"),
      path: "/municipality-map",
      permission: PERMISSIONS.MUNICIPALITY_RISK_MAP,
    },
    {
      label: t("nav.portfolioAnalysis"),
      path: "/portfolio",
      permission: PERMISSIONS.PORTFOLIO,
    },
    {
      label: t("nav.portfolioExplorer"),
      path: "/portfolio-explorer",
      permission: PERMISSIONS.PORTFOLIO_EXPLORER,
    },
    {
      label: t("nav.portfolioIntelligence"),
      path: "/portfolio-intelligence",
      permission: PERMISSIONS.PORTFOLIO_EXPLORER,
    },
    {
      label: t("nav.municipalityPortfolioAnalysis"),
      path: "/municipality-portfolio",
      permission: PERMISSIONS.MUNICIPALITY_PORTFOLIO,
    },
  ];

  // Filter nav items based on permissions
  const visibleNavItems = navItems.filter((item) =>
    hasPermission(item.permission),
  );

  React.useEffect(() => {
    const baseTitle = t("common.appName");
    let pageTitle = t("nav.riskAssessment");
    if (location.pathname.startsWith("/portfolio-intelligence")) {
      pageTitle = t("nav.portfolioIntelligence");
    } else if (location.pathname.startsWith("/portfolio-explorer")) {
      pageTitle = t("nav.portfolioExplorer");
    } else if (location.pathname.startsWith("/portfolio")) {
      pageTitle = t("nav.portfolioAnalysis");
    } else if (location.pathname.startsWith("/profile")) {
      pageTitle = t("auth.profileTitle");
    } else if (location.pathname.startsWith("/sign-in")) {
      pageTitle = t("auth.signInTitle");
    } else if (location.pathname.startsWith("/create-member")) {
      pageTitle = t("auth.createMemberTitle");
    }
    document.title = `${baseTitle} | ${pageTitle}`;
  }, [location.pathname, t]);

  React.useEffect(() => {
    const isAuthRoute =
      location.pathname.startsWith("/sign-in") ||
      location.pathname.startsWith("/create-member") ||
      location.pathname.startsWith("/legacy-private-key");
    if (!clientInfo?.token || isAuthRoute) {
      return;
    }

    refreshSession();
  }, [clientInfo?.token, location.pathname, refreshSession]);

  React.useEffect(() => {
    const nextLanguage = i18n.resolvedLanguage || "en";
    setLanguage(nextLanguage);
  }, [i18n.resolvedLanguage]);

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <MuiAppBar
        position="fixed"
        color="transparent"
        elevation={0}
        sx={{
          backgroundColor: "#fafafa",
          boxShadow:
            "0 1px 2px rgba(30, 74, 110, 0.06), 0 4px 16px rgba(46, 110, 168, 0.1)",
        }}
      >
        <Toolbar sx={{ minHeight: 72 }}>
          <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
            <img
              src={logo}
              alt={t("common.appName")}
              width={logoWidth}
              style={{ cursor: "pointer" }}
              onClick={() => navigate("/map")}
            />
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            {visibleNavItems.map((item) => {
              const active = location.pathname === item.path;
              return (
                <Button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  sx={{
                    color: active ? "primary.main" : "text.primary",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}

            <Tooltip title={t("common.language")}>
              <IconButton
                aria-label={t("common.language")}
                onClick={handleOpenLanguageMenu}
                size="large"
              >
                {languageFlag}
              </IconButton>
            </Tooltip>
            <Menu
              id="language-menu"
              anchorEl={languageAnchorEl}
              open={Boolean(languageAnchorEl)}
              onClose={handleCloseLanguageMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem onClick={() => handleLanguageSelect("en")}>
                {englishFlagSvg}
                <span style={{ marginLeft: 8 }}>English</span>
              </MenuItem>
              <MenuItem onClick={() => handleLanguageSelect("el")}>
                {greekFlagSvg}
                <span style={{ marginLeft: 8 }}>Ελληνικά</span>
              </MenuItem>
            </Menu>

            {clientInfo && (
              <>
                <Tooltip
                  title={t("nav.profileTooltip", {
                    clientId: clientInfo.clientId,
                  })}
                >
                  <IconButton
                    size="large"
                    aria-label={t("nav.accountAriaLabel")}
                    aria-controls={profileAnchorEl ? "profile-menu" : undefined}
                    aria-haspopup="true"
                    onClick={handleOpenProfileMenu}
                  >
                    <AccountCircle />
                  </IconButton>
                </Tooltip>
                <Menu
                  id="profile-menu"
                  anchorEl={profileAnchorEl}
                  open={Boolean(profileAnchorEl)}
                  onClose={handleCloseProfileMenu}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <MenuItem onClick={handleOpenProfile}>
                    {t("auth.profileTitle")}
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>
                    {t("common.logout")}
                  </MenuItem>
                </Menu>
              </>
            )}
          </Stack>
        </Toolbar>
      </MuiAppBar>

      <Main>
        <ToolbarOffset />
        {updateAvailable ? (
          <Alert
            severity="info"
            sx={{
              position: "absolute",
              top: 80,
              left: "50%",
              transform: "translateX(-50%)",
              maxWidth: "calc(100% - 32px)",
              zIndex: (theme) => theme.zIndex.snackbar,
              boxShadow: 3,
              "& .MuiAlert-action": {
                alignItems: "center",
                pt: 0,
              },
            }}
            action={
              <Button color="inherit" size="medium" onClick={reload}>
                {t("updateBanner.reload")}
              </Button>
            }
          >
            {t("updateBanner.newVersionAvailable")}
          </Alert>
        ) : null}
        {sessionWarningKey ? (
          <Alert severity="warning" sx={{ mx: 2, mt: 2 }}>
            {t(sessionWarningKey)}
          </Alert>
        ) : null}
        {children}
      </Main>
    </Box>
  );
}
